import React from 'react'
import {
  Box,
  CircularProgress,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import type {SxProps, Theme} from '@mui/material'
import {CARD_BORDER_COLOR, CARD_BORDER_RADIUS, NEUTRAL_SCALE} from 'src/theme-new/tokens'
import EmptyState from 'src/components-new/layout/EmptyState'
import Card from 'src/components-new/layout/Card'

export interface ColumnDef<T> {
  key: string
  header: string
  render: (row: T) => React.ReactNode
  width?: string | number
  isActionColumn?: boolean
  hideInMobileCard?: boolean
  /** Field name to sort by (matches the API's `sort` param). Omit for a non-sortable column. */
  sortKey?: string
  /**
   * Renders this column as the mobile card's header content (left side), replacing
   * `mobileCardTitle` for that row. Use for a composite/rich column (e.g. avatar + name)
   * that shouldn't also appear as a labeled field in the card body.
   */
  mobileCardHeader?: boolean
  /**
   * Renders this column pinned to the top-right of the mobile card header, alongside
   * any `isActionColumn` content (e.g. a status pill next to row action icons).
   */
  mobileCardBadge?: boolean
  /**
   * Groups several sub-columns under one spanning header (desktop table only), e.g.
   * a "Tipo complesso" header spanning "Betabloccante" / "Calcioantagonista" / "Disopiramide".
   * When set, this column's own `header` becomes the spanning group label and `render`/
   * `sortKey`/`width` are ignored — each sub-column supplies its own. On mobile, sub-columns
   * are flattened into the card body as ordinary fields (the group label is dropped since
   * each sub-column's own label already disambiguates it).
   */
  subColumns?: SubColumnDef<T>[]
}

export interface SubColumnDef<T> {
  key: string
  header: string
  render: (row: T) => React.ReactNode
  width?: string | number
  /** Field name to sort by (matches the API's `sort` param). Omit for a non-sortable column. */
  sortKey?: string
}

export interface PaginationState {
  page: number        // 0-based
  rowsPerPage: number
  order?: string
}

interface DataTableProps<T> {
  columns: ColumnDef<T>[]
  rows: T[]
  keyField: keyof T
  loading?: boolean
  emptyMessage?: string
  onRowClick?: (row: T) => void
  /**
   * Extra sx applied to each row's container — the desktop `TableRow` or the mobile `Card`
   * wrapper — computed per-row from its data (e.g. a background tint for unread items).
   * Merged on top of the built-in hover/cursor styles, not a replacement for them.
   */
  getRowSx?: (row: T) => SxProps<Theme> | undefined
  pagination?: PaginationState
  totalElements?: number
  onPaginationChange?: (next: PaginationState) => void
  rowsPerPageOptions?: number[]
  mobileCardTitle?: (row: T) => React.ReactNode
  /** Current sort value, e.g. `startDate` (asc) or `-startDate` (desc) — matches a column's `sortKey`. */
  sortBy?: string
  /** Called with the new sort value when a sortable column header is clicked. */
  onSortChange?: (sortBy: string) => void
  /**
   * Where `isActionColumn` content (row action icons) sits on the mobile card.
   * `'top'` (default) keeps them pinned next to the card header, alongside any
   * `mobileCardBadge` content. `'bottom'` moves them to a divided footer row
   * instead — useful when the card header has little else in it and the actions
   * read better anchored under the fields, e.g. Diario terapeutico.
   */
  mobileActionsPosition?: 'top' | 'bottom'
  /**
   * Set to drop the divider between the mobile card header and its fields — e.g. when
   * the header is already a self-contained identity block (avatar + name) and a hard
   * rule right under it reads as clutter rather than a real section break.
   */
  hideMobileCardDivider?: boolean
}

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

// Selecting text inside a cell (e.g. dragging over an email to copy it) still fires a
// plain `click` on mouse-up. Without this guard that click would also trigger row
// navigation before the user gets to copy anything. Checked once per row/card click.
function hasActiveTextSelection(): boolean {
  const selection = window.getSelection()
  return Boolean(selection && selection.toString().length > 0)
}

export default function DataTable<T,>({
  columns,
  rows,
  keyField,
  loading = false,
  emptyMessage = 'Nessun dato trovato',
  onRowClick,
  getRowSx,
  pagination,
  totalElements,
  onPaginationChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  mobileCardTitle,
  sortBy,
  onSortChange,
  mobileActionsPosition = 'top',
  hideMobileCardDivider = false,
}: DataTableProps<T>) {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const showFooter =
    pagination !== undefined && totalElements !== undefined && onPaginationChange !== undefined

  const totalPages = showFooter
    ? Math.max(1, Math.ceil(totalElements! / pagination!.rowsPerPage))
    : 1

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
        <CircularProgress />
      </Box>
    )
  }

  if (rows.length === 0) {
    return <EmptyState title="Nessun risultato" description={emptyMessage} />
  }

  if (isDesktop) {
    const hasGroups = columns.some((col) => col.subColumns && col.subColumns.length > 0)
    const headerCellSx = {
      color: NEUTRAL_SCALE[900],
      fontSize: 11,
      letterSpacing: 0.5,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      backgroundColor: NEUTRAL_SCALE[50],
      py: 1.5,
    }

    return (
      <Box sx={{borderRadius: CARD_BORDER_RADIUS, border: `1px solid ${CARD_BORDER_COLOR}`, overflow: 'hidden'}}>
        <Table>
          <TableHead>
            <TableRow sx={{backgroundColor: NEUTRAL_SCALE[50]}}>
              {columns.map((col) => {
                if (col.subColumns && col.subColumns.length > 0) {
                  return (
                    <TableCell
                      key={col.key}
                      colSpan={col.subColumns.length}
                      align="center"
                      sx={{...headerCellSx, borderBottom: `1px solid ${NEUTRAL_SCALE[200]}`}}
                    >
                      {col.header}
                    </TableCell>
                  )
                }
                const isSortable = Boolean(col.sortKey && onSortChange)
                const isDesc = sortBy === `-${col.sortKey}`
                const isAsc = sortBy === col.sortKey
                return (
                  <TableCell
                    key={col.key}
                    width={col.width}
                    rowSpan={hasGroups ? 2 : undefined}
                    sx={{...headerCellSx, verticalAlign: hasGroups ? 'bottom' : undefined}}
                  >
                    {isSortable ? (
                      <TableSortLabel
                        active={isAsc || isDesc}
                        direction={isDesc ? 'desc' : 'asc'}
                        onClick={() => onSortChange!(isAsc ? `-${col.sortKey}` : col.sortKey!)}
                      >
                        {col.header}
                      </TableSortLabel>
                    ) : (
                      col.header
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
            {hasGroups && (
              <TableRow sx={{backgroundColor: NEUTRAL_SCALE[50]}}>
                {columns.flatMap((col) => {
                  if (!col.subColumns || col.subColumns.length === 0) return []
                  return col.subColumns.map((sub) => {
                    const isSortable = Boolean(sub.sortKey && onSortChange)
                    const isDesc = sortBy === `-${sub.sortKey}`
                    const isAsc = sortBy === sub.sortKey
                    return (
                      <TableCell key={sub.key} width={sub.width} sx={headerCellSx}>
                        {isSortable ? (
                          <TableSortLabel
                            active={isAsc || isDesc}
                            direction={isDesc ? 'desc' : 'asc'}
                            onClick={() => onSortChange!(isAsc ? `-${sub.sortKey}` : sub.sortKey!)}
                          >
                            {sub.header}
                          </TableSortLabel>
                        ) : (
                          sub.header
                        )}
                      </TableCell>
                    )
                  })
                })}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={String(row[keyField])}
                // lets a page find a row on screen by its key, e.g. to animate into it (useFlyToHistory)
                data-row-key={String(row[keyField])}
                onClick={onRowClick ? () => { if (!hasActiveTextSelection()) onRowClick(row) } : undefined}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  cursor: onRowClick ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: onRowClick ? NEUTRAL_SCALE[50] : 'inherit',
                  },
                  // Plain-object merge (not the array-of-sx form) to match how Card handles
                  // `getRowSx` below — MUI's TableRow sx typing here doesn't accept an array.
                  ...(getRowSx ? (getRowSx(row) as object) : {}),
                }}
              >
                {columns.flatMap((col) => {
                  if (col.subColumns && col.subColumns.length > 0) {
                    return col.subColumns.map((sub) => (
                      <TableCell key={sub.key} sx={{py: 2}}>
                        {sub.render(row)}
                      </TableCell>
                    ))
                  }
                  return [
                    <TableCell key={col.key} sx={{py: 2}}>
                      {col.render(row)}
                    </TableCell>,
                  ]
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {showFooter && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              borderTop: `1px solid ${NEUTRAL_SCALE[200]}`,
              backgroundColor: NEUTRAL_SCALE[50],
              minHeight: 52,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Typography variant="body2" color="text.secondary">
                Righe per pagina:
              </Typography>
              <Select
                size="small"
                value={pagination!.rowsPerPage}
                onChange={(e) =>
                  onPaginationChange!({...pagination!, rowsPerPage: Number(e.target.value), page: 0})
                }
                sx={{fontSize: 13, '& .MuiSelect-select': {py: 0.5, px: 1}}}
                variant="standard"
                disableUnderline
              >
                {rowsPerPageOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </Box>

            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
              <Typography variant="body2" color="text.secondary">
                {`${pagination!.page + 1} di ${totalPages} — Totale elementi: ${totalElements}`}
              </Typography>
              <Pagination
                count={totalPages}
                page={pagination!.page + 1}
                onChange={(_e, value) =>
                  onPaginationChange!({...pagination!, page: value - 1})
                }
                color="primary"
                size="small"
              />
            </Box>
          </Box>
        )}
      </Box>
    )
  }

  // Mobile: stacked card-per-row.
  // `SubColumnDef` has no mobileCardHeader/Badge/isActionColumn flags, so grouped columns
  // are always plain content — header/badge/action detection only runs over ungrouped ones.
  const fieldLabelSx = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: 'uppercase' as const,
    color: NEUTRAL_SCALE[700],
    mb: 0.25,
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      {rows.map((row) => {
        const headerCols = columns.filter((col) => !col.subColumns && col.mobileCardHeader)
        const badgeCols = columns.filter((col) => !col.subColumns && col.mobileCardBadge)
        const actionCols = columns.filter((col) => !col.subColumns && col.isActionColumn)
        const contentCols = columns.filter(
          (col) => !col.subColumns && !col.isActionColumn && !col.hideInMobileCard && !col.mobileCardHeader && !col.mobileCardBadge
        )
        // Rendered as a separate labeled, divided sub-section below the plain fields so the
        // grouping (e.g. "Tipo complesso") stays visible instead of blending into the rest.
        const groupCols = columns.filter((col) => col.subColumns && col.subColumns.length > 0 && !col.hideInMobileCard)

        const actionsAtBottom = mobileActionsPosition === 'bottom'

        // A row may have an action column defined but nothing to show for this
        // particular row (e.g. "view document" only when a file exists) — filter
        // those out per-row so an empty header/footer strip doesn't render when
        // there's nothing in it.
        const renderedActions = actionCols
          .map((col) => ({key: col.key, node: col.render(row)}))
          .filter(({node}) => node !== null && node !== undefined && node !== false)

        // The click target wraps the whole card (see below), so interactive action
        // buttons must stop propagation or tapping them would also trigger onRowClick.
        const renderActionCols = () => renderedActions.map(({key, node}) => (
          <Box key={key} onClick={(e) => e.stopPropagation()}>{node}</Box>
        ))

        const cardAction = badgeCols.length > 0 || (renderedActions.length > 0 && !actionsAtBottom)
          ? (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              {badgeCols.map((col) => <Box key={col.key}>{col.render(row)}</Box>)}
              {!actionsAtBottom && renderActionCols()}
            </Box>
          )
          : undefined

        const cardFooter = actionsAtBottom && renderedActions.length > 0
          ? <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>{renderActionCols()}</Box>
          : undefined

        const headerContent = headerCols.length > 0
          ? (
            headerCols.length === 1
              ? headerCols[0].render(row)
              : (
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                  {headerCols.map((col) => <Box key={col.key}>{col.render(row)}</Box>)}
                </Box>
              )
          )
          : undefined

        const hasHeader = headerContent !== undefined || mobileCardTitle !== undefined || cardAction !== undefined
        const cardTitle = hasHeader ? (headerContent ?? (mobileCardTitle ? mobileCardTitle(row) : null)) : undefined

        return (
          // The click target wraps the whole card (not just the body content) so
          // navigation keeps working even when there are no body fields left to
          // render (e.g. only the composite "entity" + status columns are configured
          // — see the contentCols.length check below).
          <Box
            key={String(row[keyField])}
            data-row-key={String(row[keyField])}
            onClick={onRowClick ? () => { if (!hasActiveTextSelection()) onRowClick(row) } : undefined}
          >
            <Card
              title={cardTitle}
              action={cardAction}
              footer={cardFooter}
              hideDivider={hideMobileCardDivider}
              // Card spreads `sx` into its own object internally (Card.tsx), so it must stay
              // a plain object here — unlike the desktop TableRow above, which can take the
              // array-of-sx form MUI system props support natively.
              sx={{
                ...(onRowClick ? {cursor: 'pointer'} : {}),
                ...(getRowSx ? (getRowSx(row) as object) : {}),
              }}
            >
              {(contentCols.length > 0 || groupCols.length > 0) && (
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
                  {contentCols.length > 0 && (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: 1.5,
                      }}
                    >
                      {contentCols.map((col) => (
                        <Box key={col.key} sx={{display: 'flex', flexDirection: 'column', minWidth: 0}}>
                          <Typography sx={fieldLabelSx}>{col.header}</Typography>
                          <Box sx={{overflowWrap: 'anywhere'}}>{col.render(row)}</Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {groupCols.map((group, groupIndex) => (
                    <Box
                      key={group.key}
                      // A group right at the top of the card body (no plain contentCols
                      // above it, and it's the first group) needs no border-top — the
                      // Card component already renders a Divider between its header and
                      // body, so an extra one here would double up right under it.
                      sx={contentCols.length > 0 || groupIndex > 0
                        ? {pt: 1.5, borderTop: `1px solid ${NEUTRAL_SCALE[200]}`}
                        : undefined}
                    >
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          textTransform: 'uppercase',
                          color: NEUTRAL_SCALE[900],
                          mb: 1,
                        }}
                      >
                        {group.header}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                          gap: 1.5,
                        }}
                      >
                        {group.subColumns!.map((sub) => (
                          <Box key={sub.key} sx={{display: 'flex', flexDirection: 'column', minWidth: 0}}>
                            <Typography sx={fieldLabelSx}>{sub.header}</Typography>
                            <Box sx={{overflowWrap: 'anywhere'}}>{sub.render(row)}</Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Card>
          </Box>
        )
      })}

      {showFooter && (
        <Box sx={{display: 'flex', justifyContent: 'center', pt: 1}}>
          <Pagination
            count={totalPages}
            page={pagination!.page + 1}
            onChange={(_e, value) => onPaginationChange!({...pagination!, page: value - 1})}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  )
}
